import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { TagScopeIcon } from '../components/TagScope';
import { PencilIcon, PlusIcon, TrashIcon } from '../components/icons';
import { Banner, Button, ErrorState, Loading, PageHeader, Switch, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import type { Tag } from '../lib/types';
import { qk } from '../queries/keys';
import {
  createTag,
  deleteTag,
  fetchTags,
  renameTag,
  updateTagSettings,
  type TagList,
  type TagWithCount,
} from '../queries/tags';
import { SettingsTabs } from './SettingsPage';

/**
 * Tags, looked after in one place.
 *
 * Two lists, because they are two different things. The team's tags are
 * shared by everyone on the team: any member puts them on recordings, and
 * the owners and admins rename and delete them and say whether members may
 * make new ones. Your own tags are yours alone and nobody else ever sees
 * them, so they are yours to change however you like.
 */

export function TagsPage() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: qk.tags(), queryFn: () => fetchTags(), staleTime: 15_000 });

  useEffect(() => {
    track('Viewed Settings', { tab: 'tags' });
  }, []);

  // Every tag list, filter and shelf reads from the ['tags'] keys; the
  // library and the recording pages carry tag names too.
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['tags'] });
    void client.invalidateQueries({ queryKey: ['videos'] });
    void client.invalidateQueries({ queryKey: ['video-tags'] });
  };

  const setting = useMutation({
    mutationFn: (membersCreateTags: boolean) => updateTagSettings({ membersCreateTags }),
    onMutate: async (membersCreateTags) => {
      await client.cancelQueries({ queryKey: qk.tags() });
      const before = client.getQueryData<TagList>(qk.tags());
      if (before?.team) client.setQueryData<TagList>(qk.tags(), { ...before, team: { ...before.team, membersCreateTags } });
      return { before };
    },
    onSuccess: (_next, membersCreateTags) => track('Tag Setting Changed', { members_create_tags: membersCreateTags }),
    onError: (_error, _value, context) => {
      if (context?.before) client.setQueryData(qk.tags(), context.before);
    },
    onSettled: refresh,
  });

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="tags" />
        <Loading label="Loading your tags" />
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="tags" />
        <ErrorState title="Your tags did not load" message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      </>
    );
  }

  const { tags, team } = query.data;
  const teamTags = tags.filter((tag) => tag.scope === 'team');
  const personalTags = tags.filter((tag) => tag.scope === 'personal');

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
      <SettingsTabs active="tags" />

      <div className="space-y-6">
        {team ? (
          <section className="card p-4 sm:p-5">
            <div className="flex items-center gap-2">
              <TagScopeIcon scope="team" size={14} className="text-chalk-dim" />
              <h2 className="text-h4 text-chalk">Team tags</h2>
            </div>
            <p className="mt-1 text-caption text-chalk-dim">
              Shared with everyone on {team.name}. Anyone on the team can put these on the team’s recordings.
              {team.canManage
                ? ' Renaming or deleting one changes it on every recording it is on, for everyone.'
                : ' The team’s owners and admins rename and delete them.'}
            </p>

            <div className="mt-4 flex items-center gap-4 rounded-md border border-hairline px-3.5 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-body text-chalk">Let members create team tags</p>
                <p className="mt-0.5 text-caption text-chalk-faint">
                  {team.membersCreateTags
                    ? 'Anyone on the team can add a new team tag from a recording.'
                    : 'Only owners and admins add new team tags. Members can still make their own personal tags.'}
                  {team.canManage ? '' : ' Owners and admins change this.'}
                </p>
              </div>
              <Switch
                on={team.membersCreateTags}
                disabled={!team.canManage || setting.isPending}
                label="Let members create team tags"
                onChange={() => setting.mutate(!team.membersCreateTags)}
              />
            </div>
            {setting.isError ? (
              <div className="mt-3">
                <Banner tone="danger">{errorMessage(setting.error)}</Banner>
              </div>
            ) : null}

            <TagSection
              scope="team"
              tags={teamTags}
              canCreate={team.canCreate}
              canEdit={team.canManage}
              onChanged={refresh}
              empty={
                team.canCreate
                  ? 'No team tags yet. Add the first one below, or from any recording in the team’s library.'
                  : 'No team tags yet. Ask an owner or admin to add some.'
              }
            />
          </section>
        ) : null}

        <section className="card p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <TagScopeIcon scope="personal" size={14} className="text-chalk-dim" />
            <h2 className="text-h4 text-chalk">Your tags</h2>
          </div>
          <p className="mt-1 text-caption text-chalk-dim">
            Only you see these. Put them on anything in your library
            {team ? ', a teammate’s recording included' : ''}, and nobody else will know they are there.
          </p>
          <TagSection
            scope="personal"
            tags={personalTags}
            canCreate
            canEdit
            onChanged={refresh}
            empty="No personal tags yet. Add one below, or from the Tags tab on any recording."
          />
        </section>
      </div>
    </>
  );
}

function TagSection({
  scope,
  tags,
  canCreate,
  canEdit,
  onChanged,
  empty,
}: {
  scope: Tag['scope'];
  tags: TagWithCount[];
  canCreate: boolean;
  canEdit: boolean;
  onChanged: () => void;
  empty: string;
}) {
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => createTag(name.trim(), scope),
    onSuccess: () => {
      track('Tag Created', { scope, from: 'settings' });
      setName('');
      onChanged();
    },
  });

  return (
    <div className="mt-4">
      {tags.length === 0 ? (
        <p className="rounded-md border border-dashed border-hairline px-3.5 py-5 text-center text-caption text-chalk-faint">{empty}</p>
      ) : (
        <ul className="divide-y divide-hairline rounded-md border border-hairline">
          {tags.map((tag) => (
            <TagRow key={tag.id} tag={tag} canEdit={canEdit} onChanged={onChanged} />
          ))}
        </ul>
      )}

      {canCreate ? (
        <form
          className="mt-3 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={40}
            placeholder={scope === 'team' ? 'New team tag' : 'New personal tag'}
            aria-label={scope === 'team' ? 'New team tag' : 'New personal tag'}
            className="h-9 min-w-0 flex-1 rounded-xs border border-chalk/20 bg-slate-950/70 px-2.5 text-body text-chalk placeholder:text-chalk-faint focus:border-signal focus:outline-none"
          />
          <Button type="submit" size="sm" busy={create.isPending} disabled={!name.trim()} className="gap-1.5">
            <PlusIcon size={12} />
            Add
          </Button>
        </form>
      ) : null}
      {create.isError ? (
        <div className="mt-3">
          <Banner tone="danger">{errorMessage(create.error)}</Banner>
        </div>
      ) : null}
    </div>
  );
}

function TagRow({ tag, canEdit, onChanged }: { tag: TagWithCount; canEdit: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(tag.name);

  const rename = useMutation({
    mutationFn: () => renameTag(tag.id, name.trim()),
    onSuccess: () => {
      track('Tag Renamed', { scope: tag.scope });
      setEditing(false);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteTag(tag.id),
    onSuccess: () => {
      track('Tag Deleted', { scope: tag.scope, video_count: tag.videoCount });
      onChanged();
    },
  });
  const error = rename.error ?? remove.error;

  return (
    <li className="px-3.5 py-2.5">
      <div className="flex items-center gap-3">
        {editing ? (
          <form
            className="flex min-w-0 flex-1 gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim() && name.trim() !== tag.name) rename.mutate();
              else setEditing(false);
            }}
          >
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={40}
              autoFocus
              aria-label={`Rename ${tag.name}`}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setName(tag.name);
                  setEditing(false);
                }
              }}
              className="h-8 min-w-0 flex-1 rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-body text-chalk focus:border-signal focus:outline-none"
            />
            <Button type="submit" size="sm" variant="primary" busy={rename.isPending}>
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setName(tag.name);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </form>
        ) : (
          <>
            <span className="min-w-0 flex-1 truncate text-body text-chalk">{tag.name}</span>
            <span className="shrink-0 text-caption text-chalk-faint tnum">
              {tag.videoCount} {tag.videoCount === 1 ? 'video' : 'videos'}
            </span>
            {canEdit ? (
              confirming ? (
                <span className="flex shrink-0 items-center gap-1.5">
                  <Button size="sm" variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
                    Delete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                    Keep
                  </Button>
                </span>
              ) : (
                <span className="flex shrink-0 items-center gap-0.5">
                  <IconButton label={`Rename ${tag.name}`} onClick={() => setEditing(true)}>
                    <PencilIcon size={14} />
                  </IconButton>
                  <IconButton label={`Delete ${tag.name}`} onClick={() => setConfirming(true)}>
                    <TrashIcon size={14} />
                  </IconButton>
                </span>
              )
            ) : null}
          </>
        )}
      </div>
      {confirming ? (
        <p className="mt-1.5 text-caption text-chalk-dim">
          {tag.videoCount > 0
            ? `It comes off ${tag.videoCount === 1 ? 'the one recording' : `all ${tag.videoCount} recordings`} it is on${tag.scope === 'team' ? ', for everyone on the team' : ''}. The recordings stay.`
            : 'It is not on any recording you can see.'}
        </p>
      ) : null}
      {error ? <p className="mt-1.5 text-caption text-danger">{errorMessage(error)}</p> : null}
    </li>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cx(
        'flex h-7 w-7 items-center justify-center rounded text-chalk-faint transition-colors duration-120',
        'hover:bg-slate-800 hover:text-chalk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/60',
      )}
    >
      {children}
    </button>
  );
}
